import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface DepreciationReport {
  id: number;
  assetName: string;
  category: string;
  purchaseDate: string;
  purchaseCost: number;
  currentDepreciatedValue: number;
  annualDepreciation: number;
  accumulatedDepreciation: number;
  remainingValue: number;
  status: string;
  depreciationRate: number;
  usefulLife: number;
  yearsInUse: number;
}

export interface DepreciationStats {
  totalAssets: number;
  totalCurrentValue: number;
  totalDepreciation: number;
  totalPurchaseValue: number;
  averageDepreciationRate: number;
  categoriesBreakdown: {
    category: string;
    count: number;
    totalValue: number;
    totalDepreciation: number;
  }[];
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  total?: number;
}

export interface DepreciationReportsResponse {
  reports: DepreciationReport[];
  currentPage: number;
  totalItems: number;
  totalPages: number;
  pageSize: number;
  activeAssetsCount: number;
  totalBookValue: number;
}

@Injectable({
  providedIn: 'root'
})
export class DepreciationReportsService {
  private apiUrl = `${environment.apiServiceBaseUrl}/hawdco/depreciation-reports`;

  constructor(private http: HttpClient) {}

  // Get depreciation reports with optional pagination and filtering
  getDepreciationReports(
    page: number = 1, 
    limit: number = 10, 
    search?: string, 
    category?: string,
    status?: string,
    dateFrom?: string,
    dateTo?: string
  ): Observable<DepreciationReportsResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    
    if (search) params = params.set('search', search);
    if (category) params = params.set('category', category);
    if (status) params = params.set('status', status);
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);

    return this.http.get<DepreciationReportsResponse>(this.apiUrl, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get depreciation report by asset ID
  getDepreciationReportById(id: number): Observable<ApiResponse<DepreciationReport>> {
    return this.http.get<ApiResponse<DepreciationReport>>(`${this.apiUrl}/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get depreciation statistics and summary
  getDepreciationStats(dateFrom?: string, dateTo?: string): Observable<ApiResponse<DepreciationStats>> {
    let params = new HttpParams();
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);

    return this.http.get<ApiResponse<DepreciationStats>>(`${this.apiUrl}/stats`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Generate depreciation report for specific date
  generateDepreciationReport(reportDate: string, includeAllAssets: boolean = false): Observable<ApiResponse<DepreciationReport[]>> {
    return this.http.post<ApiResponse<DepreciationReport[]>>(`${this.apiUrl}/generate`, {
      reportDate,
      includeAllAssets
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Calculate depreciation for a specific asset
  calculateDepreciation(assetId: number, calculationDate: string): Observable<ApiResponse<{
    currentDepreciatedValue: number;
    annualDepreciation: number;
    accumulatedDepreciation: number;
    remainingValue: number;
    yearsInUse: number;
  }>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/calculate/${assetId}`, {
      calculationDate
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Update asset depreciation parameters
  updateDepreciationParameters(assetId: number, depreciationRate: number, usefulLife: number): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(`${this.apiUrl}/parameters/${assetId}`, {
      depreciationRate,
      usefulLife
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Get depreciation categories
  getDepreciationCategories(): Observable<ApiResponse<string[]>> {
    return this.http.get<ApiResponse<string[]>>(`${this.apiUrl}/categories`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Export depreciation reports to Excel
  exportToExcel(search?: string, category?: string, status?: string, dateFrom?: string, dateTo?: string): Observable<Blob> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (category) params = params.set('category', category);
    if (status) params = params.set('status', status);
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);

    return this.http.get(`${this.apiUrl}/export/excel`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Export depreciation reports to PDF
  exportToPDF(search?: string, category?: string, status?: string, dateFrom?: string, dateTo?: string): Observable<Blob> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (category) params = params.set('category', category);
    if (status) params = params.set('status', status);
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);

    return this.http.get(`${this.apiUrl}/export/pdf`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Bulk recalculate depreciation for all assets
  bulkRecalculateDepreciation(reportDate: string): Observable<ApiResponse<{
    updatedAssets: number;
    totalDepreciation: number;
    errors: string[];
  }>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/bulk-recalculate`, {
      reportDate
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Generate sample depreciation data for testing
  generateSampleDepreciationData(): Observable<DepreciationReportsResponse> {
    const sampleAssets = [
      {
        id: 1,
        assetName: 'Water Pump A1',
        category: 'Equipment',
        purchaseDate: '2022-01-15',
        purchaseCost: 15000,
        currentDepreciatedValue: 11250,
        annualDepreciation: 2250,
        accumulatedDepreciation: 3750,
        remainingValue: 11250,
        status: 'active',
        depreciationRate: 0.15,
        usefulLife: 10,
        yearsInUse: 2.5
      },
      {
        id: 2,
        assetName: 'Office Computer B2',
        category: 'IT Equipment',
        purchaseDate: '2021-06-20',
        purchaseCost: 2500,
        currentDepreciatedValue: 1250,
        annualDepreciation: 625,
        accumulatedDepreciation: 1250,
        remainingValue: 1250,
        status: 'active',
        depreciationRate: 0.25,
        usefulLife: 5,
        yearsInUse: 2.0
      },
      {
        id: 3,
        assetName: 'Vehicle C3',
        category: 'Transportation',
        purchaseDate: '2020-03-10',
        purchaseCost: 45000,
        currentDepreciatedValue: 27000,
        annualDepreciation: 9000,
        accumulatedDepreciation: 18000,
        remainingValue: 27000,
        status: 'active',
        depreciationRate: 0.20,
        usefulLife: 8,
        yearsInUse: 4.0
      }
    ];

    const mockResponse: DepreciationReportsResponse = {
      reports: sampleAssets,
      currentPage: 1,
      totalItems: 3,
      totalPages: 1,
      pageSize: 10,
      activeAssetsCount: 3,
      totalBookValue: 39500
    };

    // Return mock data for now - replace with actual API call when backend is ready
    return new Observable(observer => {
      setTimeout(() => {
        observer.next(mockResponse);
        observer.complete();
      }, 1000);
    });
  }

  // Error handling
  private handleError(error: any): Observable<never> {
    let errorMessage = 'An error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      
      if (error.error && error.error.message) {
        errorMessage = error.error.message;
      }
    }

    console.error('Depreciation Reports API Error:', error);
    return throwError(() => new Error(errorMessage));
  }
}
